import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { theme } from "config/theme";
import { COLORS } from "constants/colors";
import styled, { keyframes } from "styled-components";

export const CashLayout = styled.div`
  width: 98vw;
  height: 85vh;
  margin: 50px auto;
  background: #fff;
  border-radius: 10px;
  padding: 40px 20px 0px 20px;
  & .card-container {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 20px;
    margin: 20px 0px 25px 0px;
    flex-flow: row wrap;
    justify-content: space-between;
    ${theme.breakpoints.down("xs")} {
      overflow-x: auto;
    }
    & .skeleton {
      margin-top: -30px;
      border-radius: 10px;
    }
  }
`;

const animateHistory = keyframes`
  0% {
    top:100%;
    opacity:0;
    }
    25% {
      top:70%;
      opacity:0.4;
    }
    50% {
      top:40%;
      opacity:0.7;
    }
    75% {
      top:10%;
      opacity:0.9;
    }
    100% {
      top:0;
      opacity:1;
    }
`;
export const RemittanceLayout = styled.div`
  height: 95vh;
  background: #f5f6f8;
  border-radius: 10px;
  flex-direction: row;
  flex-wrap: wrap;
  display: flex;
  padding-top: 30px;
  & .left {
    background: #fff;
    height: 100%;
    flex: 0.8;
    margin: 50px 20px 20px 20px;
    ${theme.breakpoints.down("xs")} {
      margin: 34px 20px 0px 0px;
    }
    border-radius: 10px;
    padding: 20px;
    & .container {
      padding-top: 10px;
      border-radius: 10px;
      ${theme.breakpoints.down("xs")} {
        width: 96vw;
        height: 85vh;
        background: #fff;
        border-radius: 10px;
        padding: 10px 20px 0px 0px;
      }
      & .card-container {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin: 20px 0px;
        flex-flow: row wrap;
        ${theme.breakpoints.down("xs")} {
          overflow-x: auto;
        }
        & .skeleton {
          margin-top: -30px;
          border-radius: 10px;
        }
      }
    }
  }
  & .right {
    overflow: scroll;
    flex: 0.4;
    width: 100%;
    height: 100%;
    background: #fff;
    float: right;
    position: "relative";
    ::-webkit-scrollbar {
      display: none;
    }
  }

  & .animate-history {
    position: relative;
    animation: ${animateHistory} 0.5s ease-out;
  }
`;

export const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: -8px;
  & .header-title {
    font-weight: bold;
    ${theme.breakpoints.down("xs")} {
      font-weight: 600;
      font-size: 18px;
      padding-bottom: 15px;
    }
  }
  & .span-styled {
    font-weight: 400;
    font-size: 20;
    color: #757a93;
    ${theme.breakpoints.down("xs")} {
      font-weight: 600;
      font-size: 15px;
      color: #757a93;
    }
  }
  & .button-filter {
    ${theme.breakpoints.down("xs")} {
      padding: 6px 0px;
    }
  }
  & .header-left-container {
    display: flex;
    & .filter-button {
      height: 55px;
      border: 2px solid #399ebf;
      background: #cfecfa;
      margin-left: 20px;
      color: #399ebf;
    }
  }
`;

export const PdfImage = styled("div")`
  display: flex;
  justify-content: center;
  height: 90%;
  width: 100%;
  padding-top: 50%;
`;

export const useHeaderStyles = makeStyles<
  Theme,
  { color?: string; activeCard?: number; identifier?: number; isParcelCount?: boolean; theme?: Theme }
>(() =>
  createStyles({
    container: {
      height: "auto",
      minHeight: ({ isParcelCount }) => !isParcelCount && "120px",
      width: "auto",
      background: ({ activeCard, identifier }) => (activeCard === identifier ? "#DEEFF4" : "#F7F7F9"),
      [theme.breakpoints.down("xs")]: {
        width: "19rem",
        height: "7rem",
        padding: "0px 5px",
      },
      borderRadius: 10,
      padding: "1px 20px",
      boxSizing: "border-box",
      cursor: "pointer",
    },
    pStyled: {
      [theme.breakpoints.down("xs")]: {
        paddingTop: "8px",
      },
    },
    cardTitile: {
      color: ({ color }) => color,
      marginLeft: 5,
      fontWeight: "bold",
      fontSize: 15,
    },
    amount: {
      color: "#399EBF",
      fontWeight: "bolder",
      fontSize: 20,
      marginTop: ({ isParcelCount }) => !isParcelCount && 18,
      margin: "-8px 0px",
      float: ({ isParcelCount }) => (!isParcelCount ? "right" : "none"),
    },
    icon: {
      position: "relative",
      top: 5,
    },
    footer: {
      position: "relative",
      color: "#AAB5C4",
      top: -5,
      left: 4,
    },
  })
);

export const useCashStyles = makeStyles(() =>
  createStyles({
    table: {
      display: "grid",
      " & .MuiTableRow-head": {
        "& .MuiTableCell-head:first-child": {
          paddingLeft: "20px",
          borderBottomLeftRadius: "5px",
          borderTopLeftRadius: "5px",
        },
        "& .MuiTableCell-head:nth-last-child": {
          borderBottomRightRadius: "5px",
          borderTopRightRadius: "5px",
        },
        "& .MuiTableCell-head:last-child": {
          borderBottomRightRadius: "5px",
          borderTopRightRadius: "5px",
          border: "none",
        },
      },
      " & .MuiTableRow-root": {
        "& > td": {
          borderTop: "1px solid rgba(64, 180, 229, 0.25)",
          borderBottom: "1px solid rgba(64, 180, 229, 0.25)",
        },
        "& .MuiTableCell-root:first-child": {
          borderRadius: "5px 0px 0px 5px",
          borderLeft: "5px solid #399EBF",
        },
        "& .MuiTableCell-root:last-child": {
          borderRight: "1px solid rgba(64, 180, 229, 0.25)",
          borderRadius: "0px 5px 5px 0px",
        },
      },
      "& .MuiTableCell-head": {
        marginBottom: "1em",
        height: "40px",
        backgroundColor: COLORS.lightBlue,
        color: COLORS.primary,
      },
      "& .MuiTableRow-head .MuiTableCell-head:first-child": {
        border: "none !important",
      },
      "& .MuiTableRow-root": {
        height: 20,
      },
    },
    search: {
      marginBottom: 30,
      justifyContent: "flex-end",
      display: "flex",
    },
    dialogTitle: {
      fontWeight: "bold",
      fontSize: 25,
    },
    remittanceUid: {
      color: "#399EBF",
    },
    parcelCount: {
      color: "#F2994A",
    },
  })
);

export const useRemittanceStyles = makeStyles(() =>
  createStyles({
    table: {
      display: "grid",
      " & .MuiTableRow-head": {
        "& .MuiTableCell-head:first-child": {
          paddingLeft: "20px",
          borderBottomLeftRadius: "5px",
          borderTopLeftRadius: "5px",
        },
        "& .MuiTableCell-head:nth-last-child": {
          borderBottomRightRadius: "5px",
          borderTopRightRadius: "5px",
        },
        "& .MuiTableCell-head:last-child": {
          borderBottomRightRadius: "5px",
          borderTopRightRadius: "5px",
          border: "none",
        },
      },
      " & .MuiTableRow-root": {
        "& > td": {
          borderTop: "1px solid rgba(64, 180, 229, 0.25)",
          borderBottom: "1px solid rgba(64, 180, 229, 0.25)",
        },
        "& .MuiTableCell-root:first-child": {
          borderRadius: "5px 0px 0px 5px",
          borderLeft: "1px solid rgba(64, 180, 229, 0.25)",
        },
        "& .MuiTableCell-root:last-child": {
          borderRight: "1px solid rgba(64, 180, 229, 0.25)",
          borderRadius: "0px 5px 5px 0px",
        },
      },
      "& .MuiTableCell-head": {
        marginBottom: "1em",
        height: "40px",
        backgroundColor: COLORS.lightBlue,
        color: COLORS.primary,
      },
      "& .MuiTableRow-head .MuiTableCell-head:first-child": {
        border: "none !important",
      },
      "& .MuiTableRow-root": {
        height: 20,
      },
    },
  })
);

export const useRemittanceDetailStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: "30px 20px",
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down("xs")]: {
        padding: "0px 0px",
      },
    },
    head: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: -5,
      marginTop: -10,
    },
    remittanceTitle: {
      fontWeight: "bold",
    },
    amount: {
      fontWeight: "bold",
      color: "#F2994A",
    },
    createdAt: {
      color: "#CBCDD6",
      fontSize: "13px",
      display: "inline-block",
      marginBottom: 5,
    },
    lineHeader: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      marginTop: -10.5,
      marginBottom: -10.5,
    },
    line: {
      flex: 1,
      height: 0.5,
      background: "#399EBF",
      position: "relative",
      bottom: 3,
      marginLeft: 3,
    },
    lineLabel: {
      color: "#399EBF",
      fontWeight: "bold",
    },
    render: {
      display: "flex",
      padding: "5px 0px",
    },
    label: {
      marginRight: 8,
      color: "#CBCDD6",
    },
    value: {
      textShadow: "0px 0px 1px #757A93",
      marginRight: 8,
      color: "#757A93",
    },
    pdf: {
      cursor: "pointer",
      position: "relative",
    },
    rejectButton: {
      border: "red 1px solid",
      padding: "25px 30px",
      fontWeight: "bold",
      color: "red",
      minWidth: 125,
      fontSize: 10,
    },
    confirmButton: {
      background: "#399EBF",
      padding: "25px 30px",
      fontWeight: "bold",
      color: "#fff",
      minWidth: 125,
      fontSize: 10,
      "&:hover": {
        background: "#399EBF",
      },
    },
    footer: {
      marginTop: 30,
      display: "flex",
      justifyContent: "space-between",
    },
    noProof: {
      textAlign: "center",
      paddingTop: "20%",
      fontWeight: "bold",
      color: "#CBCDD6",
    },
    error: {
      color: "red",
      display: "flex",
      fontWeight: "bold",
    },
    notReceivedMessage: {
      color: "#757A93",
      marginTop: -5,
    },
    proofButton: {
      background: "#CFECFA",
      width: "100%",
      color: "#399EBF",
      border: "1px solid #399EBF",
      position: "relative",
      fontWeight: "bold",
      height: 58,
      margin: "5px 0px",
      "&:hover": {
        background: "#CFECFA",
      },
    },
    buttonLabel: {
      position: "absolute",
      left: 30,
    },
    history: {
      overflow: "scroll",
      height: "40vh",
      scrollbarWidth: "none",
      marginTop: 10,
      [theme.breakpoints.down("xs")]: {
        height: "16vh",
      },
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    addInfos: {},
  })
);

export const useValidationDialogStyle = makeStyles(() =>
  createStyles({
    dialog: {
      overflowY: "unset",
    },
    container: {
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      gap: 20,
    },
    progressBar: {
      color: "#fff",
    },
    yesButton: {
      height: 50,
      fontWeight: "bold",
      "&:hover": {
        background: "#399EBF",
      },
    },
    cancelButton: {
      height: 50,
      background: "#CFECF9",
      border: "1px solid #399EBF",
      margin: "15px 0px 60px 0px",
      color: "#399EBF",
      fontWeight: "bold",
      "&:hover": {
        background: "#CFECF9",
      },
    },
    message: {
      fontSize: 23,
      color: "#757A93",
      margin: "80px 0px 30px 0px",
    },
    circle: {
      height: 140,
      width: 140,
      background: "#399EBF",
      borderRadius: "50%",
      position: "absolute",
      top: 5,
      left: "50%",
      transform: "translate(-50%, -50%)",
      border: "3px solid #fff",
    },
    helperIcon: {
      fontSize: 60,
      fontWeight: "bolder",
      fontStyle: "italic",
      color: "#fff",
      position: "relative",
      left: "40%",
      top: "15%",
      transform: "translateY(-50%, -50%)",
    },
    textarea: {
      height: "inherit !important",
    },
    divMessage: {
      color: "red",
      fontSize: "14px",
      fontWeight: "bold",
      marginBottom: "10px",
    },
  })
);

export const useRemittanceColumnStyle = makeStyles(() =>
  createStyles({
    amountToRemit: {
      color: "#F2994A",
      fontWeight: "bold",
    },
    parcelCount: {
      color: "#F2994A",
      border: "1px solid #F2994A",
      padding: "3px",
      borderRadius: 5,
      cursor: "pointer",
    },
    receiveBadge: {
      background: "#E2574C",
      color: "#fff",
      padding: "1px 0px 1px 3px",
      borderRadius: "5px 0px 0px 5px",
      float: "right",
      fontWeight: 400,
      fontStyle: "italic",
      minWidth: "120px",
    },
  })
);

export const useHistoryStyle = makeStyles(() =>
  createStyles({
    container: {
      border: "1px solid #399EBF",
      height: "auto",
      borderRadius: 10,
      paddingBottom: 5,
      marginBottom: 10,
    },
    header: {
      fontSize: 15,
      textShadow: "0px 0px 1px #399EBF",
      display: "flex",
      justifyContent: "space-between",
      padding: "0px 4px",
    },

    oddHeaderBackground: {
      background: "#399EBF",
      color: "#E9F4F8",
    },

    evenHeaderBackground: {
      background: "#E9F4F8",
      color: "#399EBF",
      borderRadius: "10px 10px 0px 0px",
    },
  })
);

export const useFileStyle = makeStyles(() =>
  createStyles({
    dialog: {
      overflow: "unset",
    },
    leftArrow: {
      position: "absolute",
      height: 130,
      width: 130,
      borderRadius: "50%",
      background: "#fff",
      left: -200,
      top: "35%",
      opacity: 0.9,
      textAlign: "center",
      paddingTop: "10px",
      cursor: "pointer",
    },
    rightArrow: {
      position: "absolute",
      height: 130,
      width: 130,
      borderRadius: "50%",
      background: "#fff",
      right: -200,
      top: "35%",
      opacity: 0.9,
      textAlign: "center",
      paddingTop: "10px",
      cursor: "pointer",
    },
    downloadButton: {
      position: "absolute",
      fontWeight: 500,
      background: "#399EBF",
      color: "#fff",
      bottom: -60,
      left: "40%",
      transform: "translateX(-50%, -50%)",
      display: "flex",
      justifyContent: "space-between",
      "&:hover": {
        background: "#399EBF",
      },
      "& .title": {
        marginTop: 7,
      },
    },
    container: {
      height: "80vh",
      textAlign: "center",
    },
    img: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      margin: "auto",
    },
    closeIcon: {
      position: "absolute",
      right: -25,
      top: -25,
      "&:hover": {
        cursor: "pointer",
      },
    },
  })
);

export const HistoryItemStyle = {
  padding: "0px 10px",
  color: "#757A93",
  fontSize: 13,
};

export const Header = styled.div<{ identifier: number }>`
  font-size: 15px;
  text-shadow: 0px 0px 1px #399ebf;
  display: flex;
  justify-content: space-between;
  border-radius: ${({ identifier }) => identifier === 0 && "10px 10px 0px 0px"};
  padding: 0px 4px;
  background: ${({ identifier }) => (identifier % 2 === 0 ? "#E9F4F8" : "#399EBF")};
  color: ${({ identifier }) => (identifier % 2 === 0 ? "#399EBF" : "#E9F4F8")};
`;

export const declineIconeStyle = {
  containerBg: {
    top: -2,
    background: "#F84949",
    paddingTop: 3,
    height: 50,
    border: "1px solid inherit",
    borderRadius: "50%",
    position: "relative",
    bottom: 7,
  },
  icon: {
    color: "#fff",
    height: 15,
  },
};
